import { useEffect, useState } from "react";
import { authedFetch } from "../../lib/api-client";
import { useT } from "../../lib/use-t";

type SessionRow = {
  id: string;
  startedAt: string;
  finishedAt: string | null;
  taskType: string | null;
  module: string | null;
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("nl-NL", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function SchrijvenGeschiedenisPage() {
  const t = useT();
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    authedFetch("/schrijven/sessions")
      .then((r) => r.json())
      .then((body: { ok: boolean; data?: SessionRow[]; error?: { message: string } }) => {
        if (!body.ok) {
          setError(body.error?.message ?? "Fout bij laden");
          return;
        }
        setSessions(body.data ?? []);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Netwerk fout"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <main className="mx-auto max-w-3xl px-5 sm:px-6 py-8 sm:py-12 space-y-8">
      <div className="space-y-1">
        <p className="font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)]">
          {t("schrijven.history.eyebrow")}
        </p>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[var(--ink)]">
          {t("schrijven.history.title")}
        </h1>
      </div>

      {loading ? (
        <p className="text-sm text-[var(--ink-faint)]">{t("schrijven.history.loading")}</p>
      ) : error ? (
        <p role="alert" className="text-sm text-[var(--fail)]">{error}</p>
      ) : sessions.length === 0 ? (
        <div className="rounded-xl border border-[var(--border)] bg-[var(--panel)] p-6 text-center space-y-3">
          <p className="text-sm text-[var(--ink-muted)]">
            {t("schrijven.history.empty")}
          </p>
          <a
            href="/schrijven"
            className="inline-flex h-10 items-center rounded-[var(--radius)] bg-[var(--accent)] px-5 text-sm font-medium text-white hover:bg-[var(--accent-hover)] transition-colors"
          >
            {t("schrijven.history.startNow")}
          </a>
        </div>
      ) : (
        <ul className="space-y-3">
          {sessions.map((s) => (
            <li key={s.id}>
              <a
                href={`/schrijven/report/${s.id}`}
                className="flex items-center justify-between rounded-xl border border-[var(--border)] bg-[var(--panel)] px-5 py-4 hover:bg-[var(--panel-soft)] transition-colors"
              >
                <div>
                  <p className="text-sm font-medium text-[var(--ink)]">
                    {s.taskType ? s.taskType : t("schrijven.history.sessionTypeExam")}
                  </p>
                  <p className="text-xs text-[var(--ink-faint)] font-mono-badge mt-0.5">
                    {formatDate(s.startedAt)}
                  </p>
                </div>
                <span className="text-[var(--ink-faint)] text-xs">→</span>
              </a>
            </li>
          ))}
        </ul>
      )}

      <div className="pt-4 border-t border-[var(--border)]">
        <a
          href="/schrijven"
          className="text-sm text-[var(--ink-muted)] hover:text-[var(--ink)] transition-colors"
        >
          ← {t("schrijven.history.backToWriting")}
        </a>
      </div>
    </main>
  );
}
